import React from "react";
import { CompositionDetail, SelectedDataset, Dataset } from "../CompositionDetail";
import { CompositionTypeValue } from "./CompositionType";
import { AlertCircleIcon, CheckCircleIcon, InfoIcon, Loader2Icon, AlertTriangleIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../../../ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";

interface RLValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  stats: {
    ground_truth_datasets: number;
    adversarial_datasets: number;
    gt_unique_sessions: number;
    adv_unique_sessions: number;
    gt_total_requests: number;
    adv_total_requests: number;
    paired_sessions: number;
    paired_gt_requests: number;
    paired_adv_requests: number;
    unmatched_gt_sessions: number;
    unmatched_adv_sessions: number;
  };
}

interface CompositionBuildProps {
  availableDatasets: Dataset[];
  selectedDatasets: SelectedDataset[];
  datasetsLoading: boolean;
  compositionType?: CompositionTypeValue;
  rlValidationResult?: RLValidationResult | null;
  rlValidationLoading?: boolean;
  onAddDataset: (dataset: Dataset) => void;
  onRemoveDataset: (datasetId: number) => void;
  onConfigureDataset: (dataset: SelectedDataset) => void;
}

const CompositionBuild: React.FC<CompositionBuildProps> = ({
  availableDatasets,
  selectedDatasets,
  datasetsLoading,
  compositionType = "SFT",
  rlValidationResult,
  rlValidationLoading = false,
  onAddDataset,
  onRemoveDataset,
  onConfigureDataset,
}) => {
  const isRL = compositionType === "RL";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Build Your Composition
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Select and configure datasets to include in your composition. You can adjust sampling methods and distribution percentages.
        </p>
      </div>

      {/* RL-specific guidance */}
      {isRL && (
        <Alert className="border-purple-200 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-800">
          <InfoIcon className="h-4 w-4 text-purple-600" />
          <AlertTitle className="text-purple-900 dark:text-purple-100">RL Composition Requirements</AlertTitle>
          <AlertDescription className="text-purple-700 dark:text-purple-200">
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li>Only datasets tagged as <strong>Ground Truth</strong> or <strong>Adversarial</strong> are shown below</li>
              <li>Datasets must have matching <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">session_id</code> and <code className="bg-purple-100 dark:bg-purple-800 px-1 rounded">reference_id</code> values</li>
              <li>Paired requests must belong to the same intent</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* RL Validation Results */}
      {isRL && selectedDatasets.length > 0 && (
        <Card className="border-border">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-gc-med text-foreground flex items-center gap-2">
              {rlValidationLoading ? (
                <>
                  <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />
                  Validating RL Pairing...
                </>
              ) : rlValidationResult?.valid ? (
                <>
                  <CheckCircleIcon className="h-4 w-4 text-green-600" />
                  RL Pairing Valid
                </>
              ) : rlValidationResult ? (
                <>
                  <AlertCircleIcon className="h-4 w-4 text-red-600" />
                  RL Pairing Issues Found
                </>
              ) : (
                <>
                  <InfoIcon className="h-4 w-4 text-muted-foreground" />
                  RL Pairing Status
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {rlValidationLoading ? (
              <p className="text-sm text-muted-foreground">Checking dataset pairing compatibility...</p>
            ) : rlValidationResult ? (
              <>
                {/* Errors */}
                {rlValidationResult.errors.length > 0 && (
                  <div className="space-y-2">
                    {rlValidationResult.errors.map((error, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-red-600">
                        <AlertCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{error}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Warnings */}
                {rlValidationResult.warnings.length > 0 && (
                  <div className="space-y-2">
                    {rlValidationResult.warnings.map((warning, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-orange-600">
                        <AlertTriangleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{warning}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Stats */}
                {rlValidationResult.stats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2 border-t border-border">
                    <div>
                      <div className="text-xs text-muted-foreground">Ground Truth Datasets</div>
                      <div className="text-lg font-gc-med text-foreground">{rlValidationResult.stats.ground_truth_datasets}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Adversarial Datasets</div>
                      <div className="text-lg font-gc-med text-foreground">{rlValidationResult.stats.adversarial_datasets}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Paired Sessions</div>
                      <div className="text-lg font-gc-med text-green-600">{rlValidationResult.stats.paired_sessions}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Unmatched Sessions</div>
                      <div className="text-lg font-gc-med text-orange-600">
                        {(rlValidationResult.stats.unmatched_gt_sessions || 0) + (rlValidationResult.stats.unmatched_adv_sessions || 0)}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Add datasets to see pairing validation results.</p>
            )}
          </CardContent>
        </Card>
      )}

      <CompositionDetail
        availableDatasets={availableDatasets}
        selectedDatasets={selectedDatasets}
        datasetsLoading={datasetsLoading}
        compositionType={compositionType}
        onAddDataset={onAddDataset}
        onRemoveDataset={onRemoveDataset}
        onConfigureDataset={onConfigureDataset}
      />
    </div>
  );
};

export default CompositionBuild; 